import type {
  AuthenticationResponseJSON,
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import type {
  AnsageInput,
  AnsageTipp,
  AppConfig,
  GewinnspielZustand,
  PraesentationEinstellungen,
  KnowledgeTextInput,
  RafflePrize,
  RafflePrizeInput,
  Showcase,
  ShowcaseInput,
  TourInput,
  TourStop,
} from '@k4ia/shared'

import { NUR_LESEN_EVENT } from './umgebung'

const BASE = '/api/v1/admin'

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { credentials: 'include', ...init })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string }
    // Test liest nur: Die Meldung soll jeder sehen, egal wie die Seite Fehler
    // sonst anzeigt — der Streifen oben hebt sie hervor (NurLesenBanner).
    if (res.status === 403 && body.code === 'read_only') {
      window.dispatchEvent(new CustomEvent(NUR_LESEN_EVENT, { detail: body.error }))
    }
    throw new Error(body.error ?? `HTTP ${res.status}`)
  }
  return res.json() as Promise<T>
}

// Showcases
export const listShowcases = () => jsonFetch<{ showcases: Showcase[] }>('/showcases')
export const getShowcase = (id: string) =>
  jsonFetch<{ showcase: Showcase; assets: AdminAsset[] }>(`/showcases/${id}`)
export const createShowcase = (body: ShowcaseInput) =>
  jsonFetch<{ id: string }>('/showcases', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
export const updateShowcase = (id: string, body: Partial<ShowcaseInput>) =>
  jsonFetch<{ ok: true }>(`/showcases/${id}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
export const deleteShowcase = (id: string) =>
  jsonFetch<{ ok: true }>(`/showcases/${id}`, { method: 'DELETE' })

export interface AdminAsset {
  id: string
  showcaseId: string
  type: 'image' | 'video' | 'pdf' | 'reference_image'
  r2Key: string
  description: string
  language: string
  angleLabel: string | null
  createdAt: string
}

/** Die Datei eines Mediums (für den Word-Export). */
export async function fetchAssetFile(showcaseId: string, assetId: string): Promise<Blob> {
  const res = await fetch(`${BASE}/showcases/${showcaseId}/assets/${assetId}/file`, {
    credentials: 'include',
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error ?? `HTTP ${res.status}`)
  }
  return res.blob()
}

export async function uploadAsset(
  showcaseId: string,
  file: File,
  type: AdminAsset['type'],
  description?: string,
): Promise<{ id: string; r2Key: string }> {
  const form = new FormData()
  form.append('file', file)
  form.append('type', type)
  if (description) form.append('description', description)
  return jsonFetch(`/showcases/${showcaseId}/assets`, { method: 'POST', body: form })
}

/** „powered by": Logo des Partners hochladen (ersetzt ein vorhandenes). */
export async function uploadPartnerLogo(showcaseId: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  return jsonFetch<{ partnerLogoKey: string }>(`/showcases/${showcaseId}/partner-logo`, {
    method: 'POST',
    body: form,
  })
}

export const deletePartnerLogo = (showcaseId: string) =>
  jsonFetch<{ ok: true }>(`/showcases/${showcaseId}/partner-logo`, { method: 'DELETE' })

/** Vorschau des Logos; `stand` macht die Adresse nach einem neuen Upload neu. */
export const partnerLogoUrl = (showcaseId: string, stand: string) =>
  `${BASE}/showcases/${showcaseId}/partner-logo?v=${encodeURIComponent(stand)}`

export const deleteAsset = (showcaseId: string, assetId: string) =>
  jsonFetch<{ ok: true }>(`/showcases/${showcaseId}/assets/${assetId}`, { method: 'DELETE' })

// Documents
export interface AdminDocument {
  id: string
  showcaseId: string | null
  title: string
  source: string
  r2Key: string | null
  status: 'uploaded' | 'indexing' | 'indexed' | 'error'
  version: number
  errorMessage: string | null
  /** Thema für Wissen ohne Showcase (KNOWLEDGE_TOPICS); null bei Showcase-Dokumenten. */
  topic: string | null
  /** Im Admin getippter Text; null bei Datei- und URL-Dokumenten. */
  body: string | null
  /** Webseiten: wann zuletzt abgerufen und wann sich der Text zuletzt geändert hat. */
  checkedAt: string | null
  changedAt: string | null
  createdAt: string
  updatedAt: string
}

export const listDocuments = () => jsonFetch<{ documents: AdminDocument[] }>('/documents')
export const reindexDocument = (id: string) =>
  jsonFetch<{ ok: true }>(`/documents/${id}/reindex`, { method: 'POST' })
/** Alle Dokumente (Showcase-Dokumente und Wissen) neu in die Warteschlange. */
export const reindexAllDocuments = () =>
  jsonFetch<{ ok: true; count: number }>('/documents/reindex-all', { method: 'POST' })
/** Webseite jetzt auf Änderungen prüfen (sonst nachts von selbst). */
export const checkDocument = (id: string) =>
  jsonFetch<{ ok: true }>(`/documents/${id}/check`, { method: 'POST' })
export const deleteDocument = (id: string) =>
  jsonFetch<{ ok: true }>(`/documents/${id}`, { method: 'DELETE' })
export const listDocumentChunks = (id: string) =>
  jsonFetch<{ chunks: { id: string; text: string; position: number }[] }>(`/documents/${id}/chunks`)

export async function uploadDocumentFile(
  file: File,
  title?: string,
  showcaseId?: string,
  topic?: string,
): Promise<{ id: string }> {
  const form = new FormData()
  form.append('file', file)
  if (title) form.append('title', title)
  if (showcaseId) form.append('showcaseId', showcaseId)
  if (topic) form.append('topic', topic)
  return jsonFetch('/documents', { method: 'POST', body: form })
}

export const uploadDocumentUrl = (
  url: string,
  title?: string,
  showcaseId?: string,
  topic?: string,
) =>
  jsonFetch<{ id: string }>('/documents', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ url, title, showcaseId, topic }),
  })

/** Wissenseintrag als getippter Text (Seite „Wissen") — wird wie eine Datei zerteilt und indexiert. */
export const createKnowledgeText = (input: KnowledgeTextInput) =>
  jsonFetch<{ id: string }>('/documents', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  })

export const updateDocument = (id: string, input: Partial<KnowledgeTextInput>) =>
  jsonFetch<{ ok: true; reindexed: boolean }>(`/documents/${id}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  })

// NFC tags
export interface AdminNfcTag {
  id: string
  tagUid: string
  target: 'start' | 'showcase'
  showcaseId: string | null
  location: string | null
  createdAt: string
}

export const listNfcTags = () => jsonFetch<{ nfcTags: AdminNfcTag[]; baseUrl: string }>('/nfc-tags')
export const createNfcTag = (body: {
  /** Leer: der Server leitet sie aus dem Ziel ab („eingang", Showcase-Slug). */
  tagUid?: string
  target: 'start' | 'showcase'
  showcaseId?: string
  location?: string
}) =>
  jsonFetch<{ id: string }>('/nfc-tags', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
export const deleteNfcTag = (id: string) =>
  jsonFetch<{ ok: true }>(`/nfc-tags/${id}`, { method: 'DELETE' })

// Erkennung (Kamera lernt mit)
export interface AdminRecognitionShowcase {
  showcaseId: string
  title: string
  slug: string
  visualDescription: string
  recognized: number
  bySmall: number
  byLlm: number
  confirmed: number
  declined: number
  newHints: number
  proposals: Array<{ id: string; text: string; createdAt: string }>
  lastRecognizedAt: string | null
}
export interface AdminRecognitionOverview {
  days: number
  none: { loop: number; button: number; photo: number }
  groups: number
  showcases: AdminRecognitionShowcase[]
}
export const getRecognitionOverview = (days = 30) =>
  jsonFetch<AdminRecognitionOverview>(`/recognition?days=${days}`)
export const proposeRecognitionUpdate = (showcaseId: string) =>
  jsonFetch<{ status: string; hinweise?: number; text?: string }>(
    `/recognition/${showcaseId}/propose`,
    { method: 'POST' },
  )
export const acceptRecognitionProposal = (id: string) =>
  jsonFetch<{ ok: true }>(`/recognition/proposals/${id}/accept`, { method: 'POST' })
export const dismissRecognitionProposal = (id: string) =>
  jsonFetch<{ ok: true }>(`/recognition/proposals/${id}/dismiss`, { method: 'POST' })

// Rundgang
export interface AdminTour {
  stops: TourStop[]
  showcases: Array<{ id: string; slug: string; title: string; status: 'draft' | 'live' }>
}
export const getTour = () => jsonFetch<AdminTour>('/tour')
export const saveTour = (input: TourInput) =>
  jsonFetch<{ stops: TourStop[] }>('/tour', {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(input),
  })

// Teams-Kanäle (Meldekanal wählen)
export interface AdminTeamsChannel {
  conversationId: string
  serviceUrl: string
  conversationType: string
  teamName: string | null
  channelName: string | null
  lastSeenAt: string
}
export const listTeamsChannels = () =>
  jsonFetch<{ channels: AdminTeamsChannel[]; fromEnv: boolean; botConfigured: boolean }>(
    '/config/teams-channels',
  )

export interface TestMailResult {
  ok: boolean
  from?: string
  error?: string
  hint?: string
}
export const sendTestMail = (to: string) =>
  jsonFetch<TestMailResult>('/config/test-mail', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ to }),
  })

export interface MailLogEntry {
  /** 'zusammenfassung' oder 'bestaetigung' */
  art: string
  ok: boolean
  at: string
  error?: string
  hint?: string
}
/** Die letzten Mails aus dem Chat — mit Microsofts Grund, wenn eine scheiterte. */
export const getMailLog = () => jsonFetch<{ entries: MailLogEntry[] }>('/config/mail-log')

export interface TestSmensoResult {
  ok: boolean
  reference?: string | null
  error?: string
  hint?: string
  /** Anfang der Key-ID des hinterlegten Schlüssels — kein Geheimnis. */
  keyId?: string | null
}
/** „Smenso testen“: legt eine Testaufgabe im Projekt an. */
export const sendTestSmenso = () =>
  jsonFetch<TestSmensoResult>('/config/test-smenso', { method: 'POST' })

export interface TestEslResult {
  ok: boolean
  /** Artikel in der Collection J4P (laut Anleitung 47). */
  count?: number
  /** Drei Beispiele „FA001 Name (Preis)“ — zeigt, dass die Antwortform stimmt. */
  sample?: string[]
  error?: string
  hint?: string
}
/** „Preisschilder testen“: liest den Katalog des ESL-Preisdienstes, ändert nichts. */
export const sendTestEsl = () => jsonFetch<TestEslResult>('/config/test-esl', { method: 'POST' })

export interface TestCallResult {
  ok: boolean
  name?: string
  conversationId?: string
  error?: string
  hint?: string
}
/** Testanruf: ohne Nummer an die erste Person der Rufbereitschaft. */
export const sendTestCall = (to?: string) =>
  jsonFetch<TestCallResult>('/config/test-call', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(to ? { to } : {}),
  })

// Config
export const getConfig = () => jsonFetch<{ config: AppConfig }>('/config')
/** `/intro`: die Seite für den Bildschirm und die URL fürs Intro-Tag (mit Token). */
export const getIntroLinks = () =>
  jsonFetch<{
    screenUrl: string
    triggerUrl: string
    schaufensterUrl: string
    /** Gemeinsame Fernbedienung (`/buehne`) für Intro, Gespräch und Gewinnspiel. */
    buehne: { screenUrl: string; remoteUrl: string }
    /** Team-Link: öffnet den normalen Chat als Team-Sitzung (Preise ändern). */
    teamUrl: string
  }>('/intro')
export const updateConfig = (
  updates: Partial<Record<keyof AppConfig, string | number | boolean | null>>,
) =>
  jsonFetch<{ config: AppConfig }>('/config', {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(updates),
  })

// System prompts
export interface AdminSystemPrompt {
  id: string
  version: string
  text: string
  isActive: boolean
  createdAt: string
  createdBy: string | null
}

export const listPrompts = () => jsonFetch<{ prompts: AdminSystemPrompt[] }>('/config/prompts')
export const createPrompt = (version: string, text: string) =>
  jsonFetch<{ id: string }>('/config/prompts', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ version, text }),
  })
export const activatePrompt = (id: string) =>
  jsonFetch<{ ok: true }>(`/config/prompts/${id}/activate`, { method: 'POST' })

export function testPrompt(promptText: string, message: string): Promise<Response> {
  return fetch(`${BASE}/config/prompts/test`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify({ promptText, message }),
  })
}

// Analytics (SPEC "Analytics" + "Wissenslücken-Report" + "Gespräche")
export interface AdminKnowledgeGap {
  id: string
  question: string
  occurrenceCount: number
  status: 'open' | 'resolved'
  createdAt: string
  updatedAt: string
}

export interface AdminAnalyticsSummary {
  sessionsPerDay: { day: string; count: number }[]
  topShowcases: { showcaseId: string; title: string; visits: number }[]
  topQuestions: { question: string; count: number }[]
  knowledgeGaps: AdminKnowledgeGap[]
  feedback: { up: number; down: number }
  usage: {
    sessions: number
    assistantMessages: number
    avgLatencyMs: number | null
    totalTokensIn: number
    totalTokensOut: number
    generalKnowledgeShare: number
  }
  cost: {
    todayEur: number
    limitPerDayEur: number
    limitPerSessionEur: number
    limitedTurnsToday: number
    perDay: { day: string; costEur: number; tokensIn: number; tokensOut: number }[]
  }
}

export const getAnalytics = (days = 30) =>
  jsonFetch<{ days: number; summary: AdminAnalyticsSummary }>(`/analytics?days=${days}`)

export interface AdminLiveOverview {
  activeNow: number
  sessionsToday: number
  sessions: Array<{
    id: string
    channel: string
    startedAt: string
    lastMessageAt: string | null
    showcase: string | null
    lastQuestion: string | null
    turns: number
    groupMode: boolean
  }>
}
export const getLive = () => jsonFetch<AdminLiveOverview>('/analytics/live')

export const resolveKnowledgeGap = (id: string) =>
  jsonFetch<{ ok: true }>(`/analytics/knowledge-gaps/${id}/resolve`, { method: 'POST' })

export interface AdminConversationSession {
  id: string
  channel: string
  locale: string
  startedAt: string
  endedAt: string | null
  visitedShowcaseIds: string[]
  groupMode: boolean
}

export interface AdminConversationMessage {
  id: string
  role: string
  content: string
  generalKnowledge: boolean
  latencyMs: number | null
  createdAt: string
}

export const listConversations = () =>
  jsonFetch<{ sessions: AdminConversationSession[] }>('/analytics/conversations')

export const getConversation = (id: string) =>
  jsonFetch<{ session: AdminConversationSession; messages: AdminConversationMessage[] }>(
    `/analytics/conversations/${id}`,
  )

const jsonHeaders = { 'content-type': 'application/json' }

// Anmeldung per Passwort (ADR 0010) — nur aktiv, wo kein Cloudflare Access
// davorsteht. Diese drei Aufrufe liegen vor der Access-Prüfung und brauchen
// deshalb keine Anmeldung.
export const adminLoginMode = () =>
  jsonFetch<{ access?: boolean; passwordLogin: boolean; passkeyLogin?: boolean }>('/login')

// Passkeys (Face ID, Fingerabdruck, Sicherheitsschlüssel; workers/api/src/lib/passkeys.ts).
// Die Optionen und Antworten sind die JSON-Formen aus @simplewebauthn; das Admin
// reicht sie nur zwischen Server und `navigator.credentials` durch.
export interface AdminPasskey {
  id: string
  name: string
  createdAt: string
  lastUsedAt: string | null
  backedUp: boolean
}
export const passkeyLoginOptions = () =>
  jsonFetch<{ options: PublicKeyCredentialRequestOptionsJSON }>('/login/passkey/optionen', {
    method: 'POST',
  })
export const passkeyLogin = (response: AuthenticationResponseJSON) =>
  jsonFetch<{ ok: true; name: string }>('/login/passkey', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(response),
  })
export const listPasskeys = () => jsonFetch<{ passkeys: AdminPasskey[] }>('/passkeys')
export const passkeyRegisterOptions = (name: string) =>
  jsonFetch<{ options: PublicKeyCredentialCreationOptionsJSON }>('/passkeys/optionen', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ name }),
  })
export const passkeyRegister = (name: string, response: RegistrationResponseJSON) =>
  jsonFetch<{ ok: true; id: string; name: string }>('/passkeys', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ name, response }),
  })
export const deletePasskey = (id: string) =>
  jsonFetch<{ ok: true }>(`/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' })

export const adminLogin = (user: string, password: string) =>
  jsonFetch<{ ok: true }>('/login', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ user, password }),
  })

export const adminLogout = () => jsonFetch<{ ok: true }>('/login', { method: 'DELETE' })

// „Nach Test synchronisieren“ (nur im Production-Admin, workers/api/src/lib/content-sync.ts)
export const getTestSync = () =>
  jsonFetch<{ moeglich: boolean; grund: string | null }>('/test-sync')
export const testSyncInhalte = () =>
  jsonFetch<{ anzahl: Record<string, number>; fehlend: string[] }>('/test-sync/inhalte', {
    method: 'POST',
  })
export const testSyncDateien = (keys: string[]) =>
  jsonFetch<{ kopiert: number; fehltHier: string[]; fehler: { key: string; error: string }[] }>(
    '/test-sync/dateien',
    { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ keys }) },
  )
export const testSyncAbschluss = () =>
  jsonFetch<{ dokumente: number }>('/test-sync/abschluss', { method: 'POST' })
export interface SyncAenderungen {
  letzterAbgleich: string | null
  letzterAbgleichVon: string | null
  letzterFehler: { at: string; text: string } | null
  anzahl: number
  eintraege: { at: string; actor: string; text: string }[]
}
export const getTestSyncAenderungen = () => jsonFetch<SyncAenderungen>('/test-sync/aenderungen')
/** Nach einem Abgleich: Hinweis und Liste neu laden. */
export const TEST_SYNC_FERTIG_EVENT = 'k4ia:test-sync-fertig'

// Gewinnspiel (Admin → Gewinnspiel; Bildschirm /gewinnspiel, Handy /gewinnspiel/go/<token>)
export interface AdminGewinnspiel {
  preise: RafflePrize[]
  /** Teilnehmer je Preis. */
  teilnehmer: Record<string, string[]>
  ziehung: GewinnspielZustand
  screenUrl: string
  remoteUrl: string
  /** Gemeinsame Fernbedienung (`/buehne`) für Intro, Gespräch und Gewinnspiel. */
  buehne: { screenUrl: string; remoteUrl: string }
}

export const getGewinnspiel = () => jsonFetch<AdminGewinnspiel>('/gewinnspiel')

export const createPreis = (input: RafflePrizeInput) =>
  jsonFetch<{ preise: RafflePrize[] }>('/gewinnspiel/preise', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })

export const updatePreis = (id: string, input: RafflePrizeInput) =>
  jsonFetch<{ preise: RafflePrize[] }>(`/gewinnspiel/preise/${id}`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  })

export const deletePreis = (id: string) =>
  jsonFetch<{ preise: RafflePrize[] }>(`/gewinnspiel/preise/${id}`, { method: 'DELETE' })

export const reorderPreise = (ids: string[]) =>
  jsonFetch<{ preise: RafflePrize[] }>('/gewinnspiel/preise-reihenfolge', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ids }),
  })

export async function uploadPreisLogo(id: string, file: File) {
  const form = new FormData()
  form.append('file', file)
  return jsonFetch<{ preise: RafflePrize[] }>(`/gewinnspiel/preise/${id}/logo`, {
    method: 'POST',
    body: form,
  })
}

/** Vorschau des Logos über die Admin-Adresse; der Schlüssel macht die Adresse nach einem neuen Upload neu. */
export const preisLogoUrl = (id: string, logoKey: string) =>
  `${BASE}/gewinnspiel/preise/${id}/logo?v=${encodeURIComponent(logoKey)}`

export const deletePreisLogo = (id: string) =>
  jsonFetch<{ preise: RafflePrize[] }>(`/gewinnspiel/preise/${id}/logo`, { method: 'DELETE' })

/** Die Teilnehmerliste eines Preises ersetzen — die Namen, wie sie aus der Datei gelesen wurden. */
export const saveTeilnehmer = (preisId: string, handles: string[]) =>
  jsonFetch<{
    angenommen: number
    doppelt: number
    verworfen: string[]
    teilnehmer: Record<string, string[]>
    preise: RafflePrize[]
  }>(`/gewinnspiel/preise/${preisId}/teilnehmer`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ handles }),
  })

// --- Präsentation (routes/admin/praesentation.ts) ---

export interface AdminPraesentationFolie {
  nr: number
  titel: string
  text: string
  bild: boolean
}

export interface AdminPraesentation {
  folien: AdminPraesentationFolie[]
  einstellungen: PraesentationEinstellungen
  stand: {
    phase: 'bereit' | 'laeuft' | 'fragen' | 'fertig'
    folie: number
    bildschirm: { folie: number; spricht: boolean; pausiert: boolean }
  }
  buehne: { screenUrl: string; remoteUrl: string }
  /** Sind ELEVENLABS_API_KEY/AGENT_ID gesetzt? Sonst zeigt die Bühne nur, K4ia bleibt stumm. */
  sprachausgabe: boolean
}

export interface SkriptErgebnis {
  folien: number
  von: number
  bis: number
  kurzfassung: boolean
  quartale: { von: number; bis: number } | null
  verworfen: string[]
}

export const getPraesentation = () => jsonFetch<AdminPraesentation>('/praesentation')

export const importPraesentationSkript = (text: string) =>
  jsonFetch<SkriptErgebnis>('/praesentation/skript', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ text }),
  })

export const updatePraesentationFolie = (nr: number, werte: { titel?: string; text?: string }) =>
  jsonFetch<{ ok: true }>(`/praesentation/folien/${nr}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(werte),
  })

export async function uploadPraesentationBild(nr: number, file: File) {
  const form = new FormData()
  form.append('file', file)
  return jsonFetch<{ ok: true }>(`/praesentation/folien/${nr}/bild`, { method: 'POST', body: form })
}

/** Vorschau über die Admin-Adresse; `v` erneuert die Adresse nach einem Upload. */
export const praesentationBildUrl = (nr: number, v: boolean | string) =>
  `${BASE}/praesentation/folien/${nr}/bild?v=${encodeURIComponent(String(v))}`

export const deletePraesentationFolie = (nr: number) =>
  jsonFetch<{ ok: true }>(`/praesentation/folien/${nr}`, { method: 'DELETE' })

export const updatePraesentationEinstellungen = (werte: Partial<PraesentationEinstellungen>) =>
  jsonFetch<{ einstellungen: PraesentationEinstellungen }>('/praesentation/einstellungen', {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(werte),
  })

export const deletePraesentation = () =>
  jsonFetch<{ ok: true }>('/praesentation', { method: 'DELETE' })

/** Eine Tonspur erzeugen und ablegen lassen; die MP3 selbst wird hier nicht gebraucht. */
export async function praesentationTonVorbereiten(quelle: number | 'kurzfassung'): Promise<void> {
  const pfad =
    quelle === 'kurzfassung'
      ? '/praesentation/kurzfassung/ton'
      : `/praesentation/folien/${quelle}/ton`
  const res = await fetch(`${BASE}${pfad}`, { credentials: 'include', cache: 'no-store' })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; hinweis?: string }
    throw new Error([body.error ?? `HTTP ${res.status}`, body.hinweis].filter(Boolean).join(' '))
  }
  // Ganz lesen: Erst dann ist auch die Ablage in R2 durch (der Worker teilt den Strom).
  await res.arrayBuffer()
}

// --- Ansagen (routes/admin/ansagen.ts) ---

export interface AdminAnsage {
  id: string
  title: string
  text: string
  url: string
  /** Auslosung nach der Ansage: Mannschaften, festes Ergebnis, Satz danach. */
  tipp: AnsageTipp
  /** Logos über /media der Besucher-App (null = keins). */
  teamALogo: string | null
  teamBLogo: string | null
  createdAt: string
  updatedAt: string
}

export const listAnsagen = () =>
  jsonFetch<{ ansagen: AdminAnsage[]; sprachausgabe: boolean }>('/ansagen')
export const createAnsage = (werte: AnsageInput) =>
  jsonFetch<{ ansage: AdminAnsage }>('/ansagen', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(werte),
  })
export const updateAnsage = (id: string, werte: AnsageInput) =>
  jsonFetch<{ ok: true }>(`/ansagen/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: jsonHeaders,
    body: JSON.stringify(werte),
  })
export const deleteAnsage = (id: string) =>
  jsonFetch<{ ok: true }>(`/ansagen/${encodeURIComponent(id)}`, { method: 'DELETE' })

export async function uploadAnsageLogo(id: string, seite: 'a' | 'b', file: File) {
  const form = new FormData()
  form.append('file', file)
  return jsonFetch<{ ok: true; logo: string }>(`/ansagen/${encodeURIComponent(id)}/logo/${seite}`, {
    method: 'POST',
    body: form,
  })
}
export const deleteAnsageLogo = (id: string, seite: 'a' | 'b') =>
  jsonFetch<{ ok: true }>(`/ansagen/${encodeURIComponent(id)}/logo/${seite}`, {
    method: 'DELETE',
  })

/** Die Tonspur einmal erzeugen und ablegen lassen, damit der Link sofort spricht (`tipp`: der Satz nach der Auslosung). */
export async function ansageTonVorbereiten(
  id: string,
  was: 'text' | 'tipp' = 'text',
): Promise<void> {
  const res = await fetch(
    `${BASE}/ansagen/${encodeURIComponent(id)}/${was === 'tipp' ? 'tipp/ton' : 'ton'}`,
    {
      credentials: 'include',
      cache: 'no-store',
    },
  )
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; hinweis?: string }
    throw new Error([body.error ?? `HTTP ${res.status}`, body.hinweis].filter(Boolean).join(' '))
  }
  await res.arrayBuffer()
}
